import { formatErrorMessage } from "@agrigate/ts-utils/lib/error";
import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import { toaster } from "../components/ui/toaster";

export interface MessagesInput {
    success?: MaybeN<string | boolean>;
    error?: MaybeN<string | boolean>;
}

export interface NotifyOpts<T> {
    swallowError?: boolean;
    /**
     * If true - Use default messages to notify both success and error
     * If { error: true } - Use default message to notify error, do not notify success
     * If { error: "..." } - Use specific message to notify error, do not notify success
     * If { error: "...", success: "..." } - Use specific messages to notify success and error
     */
    messages?: boolean | MessagesInput;
    onSuccess?: (res: T) => void;
    onError?: (error: unknown) => void;
}

const defaultMessages = {
    success: "Operation Successful",
    error: "Operation failed",
};

export function usePromiseToast() {
    const withToast = async <T>(
        thunk: () => Promise<T>,
        opts?: NotifyOpts<T>,
    ) => {
        try {
            const res = await thunk();
            const msg = getSuccessMsg(opts?.messages);
            if (msg) {
                toaster.create({
                    ...msg,
                    type: "success",
                    closable: true,
                });
            }
            opts?.onSuccess?.(res);
            return res;
        } catch (e: unknown) {
            toaster.create({
                ...getErrMsg(opts?.messages, e),
                type: "error",
                closable: true,
            });
            if (opts?.onError) opts.onError(e);
            else if (!opts?.swallowError) throw e;
        }
    };
    return {
        withToast,
    };
}

const getSuccessMsg = (msgs: MaybeN<MessagesInput | boolean>) => {
    if (msgs === true) return { title: defaultMessages.success };
    if (msgs === false) return null;
    if (msgs?.success === true) return { title: defaultMessages.success };
    if (!msgs?.success) return null;
    return { title: msgs.success };
};

const getErrMsg = (msgs: MaybeN<MessagesInput | boolean>, error: unknown) => {
    if (msgs === true) {
        return {
            title: defaultMessages.error,
            description: formatErrorMessage(error),
        };
    }
    if (msgs === false) {
        return {
            title: defaultMessages.error,
        };
    }
    if (!msgs || msgs?.error === true) {
        return {
            title: defaultMessages.error,
            description: formatErrorMessage(error),
        };
    }
    if (typeof msgs.error === "string") {
        return {
            title: msgs.error,
            description: formatErrorMessage(error),
        };
    }
    return {
        title: defaultMessages.error,
    };
};
